export const fontFamily = {
  sans:    "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  display: "'Intel One Display', 'Inter', system-ui, sans-serif",
  mono:    "'JetBrains Mono', 'Fira Code', ui-monospace, monospace",
} as const;

export const fontWeight = {
  regular:   400,
  medium:    500,
  semibold:  600,
  bold:      700,
  extrabold: 800,
} as const;

export interface TypeStyle {
  fontFamily: string;
  fontSize: string;
  lineHeight: string | number;
  letterSpacing: string;
  fontWeight: number;
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
}

/**
 * The back-office specimen scale: /styleguide renders it and the MUI theme
 * mirrors it. Kept as it is; the Figma scale is `textStyle` below.
 */
export const typeScale: Record<string, TypeStyle> = {
  h1:       { fontFamily: fontFamily.display, fontSize: '56px', lineHeight: 1.10, letterSpacing: '-0.02em', fontWeight: fontWeight.extrabold },
  h2:       { fontFamily: fontFamily.display, fontSize: '40px', lineHeight: 1.15, letterSpacing: '-0.02em', fontWeight: fontWeight.extrabold },
  h3:       { fontFamily: fontFamily.display, fontSize: '32px', lineHeight: 1.20, letterSpacing: '-0.01em', fontWeight: fontWeight.bold },
  h4:       { fontFamily: fontFamily.sans,    fontSize: '24px', lineHeight: 1.25, letterSpacing: '-0.01em', fontWeight: fontWeight.bold },
  'body-l': { fontFamily: fontFamily.sans,    fontSize: '18px', lineHeight: 1.50, letterSpacing: '0',       fontWeight: fontWeight.regular },
  'body-m': { fontFamily: fontFamily.sans,    fontSize: '16px', lineHeight: 1.50, letterSpacing: '0',       fontWeight: fontWeight.regular },
  'body-s': { fontFamily: fontFamily.sans,    fontSize: '14px', lineHeight: 1.45, letterSpacing: '0',       fontWeight: fontWeight.regular },
  caption:  { fontFamily: fontFamily.sans,    fontSize: '12px', lineHeight: 1.40, letterSpacing: '0',       fontWeight: fontWeight.regular },
  overline: { fontFamily: fontFamily.sans,    fontSize: '12px', lineHeight: 1.40, letterSpacing: '0.24em',  fontWeight: fontWeight.semibold, textTransform: 'uppercase' },
};

export type TypeScaleKey = keyof typeof typeScale;

/** Figma size/* — also --font-size-* in tokens.css. */
export const fontSize = {
  'display-l': '72px',
  h1:          '56px',
  h2:          '40px',
  h3:          '28px',
  'body-l':    '18px',
  'body-m':    '16px',
  'body-s':    '14px',
  caption:     '13px',
  micro:       '12px',
} as const;

/** Figma lh/* — also --lh-* in tokens.css. */
export const lineHeight = {
  tight:   1.05,
  snug:    1.1,
  base:    1.15,
  relaxed: 1.2,
  body:    1.4,
  loose:   1.5,
} as const;

/** Figma ls/* — also --ls-* in tokens.css. */
export const letterSpacing = {
  display: '-0.02em',
  heading: '-0.01em',
  body:    '0',
  eyebrow: '0.24em',
} as const;

/** The nine Figma text styles. All Inter; the customer app's type scale. */
export const textStyle = {
  'display-l': { fontFamily: fontFamily.sans, fontSize: fontSize['display-l'], lineHeight: lineHeight.tight,   letterSpacing: letterSpacing.display, fontWeight: fontWeight.extrabold },
  h1:          { fontFamily: fontFamily.sans, fontSize: fontSize.h1,          lineHeight: lineHeight.snug,    letterSpacing: letterSpacing.display, fontWeight: fontWeight.extrabold },
  h2:          { fontFamily: fontFamily.sans, fontSize: fontSize.h2,          lineHeight: lineHeight.base,    letterSpacing: letterSpacing.heading, fontWeight: fontWeight.semibold },
  h3:          { fontFamily: fontFamily.sans, fontSize: fontSize.h3,          lineHeight: lineHeight.relaxed, letterSpacing: letterSpacing.heading, fontWeight: fontWeight.semibold },
  'body-l':    { fontFamily: fontFamily.sans, fontSize: fontSize['body-l'],    lineHeight: lineHeight.loose,   letterSpacing: letterSpacing.body,    fontWeight: fontWeight.regular },
  'body-m':    { fontFamily: fontFamily.sans, fontSize: fontSize['body-m'],    lineHeight: lineHeight.loose,   letterSpacing: letterSpacing.body,    fontWeight: fontWeight.regular },
  'body-s':    { fontFamily: fontFamily.sans, fontSize: fontSize['body-s'],    lineHeight: lineHeight.body,    letterSpacing: letterSpacing.body,    fontWeight: fontWeight.regular },
  caption:     { fontFamily: fontFamily.sans, fontSize: fontSize.caption,     lineHeight: lineHeight.body,    letterSpacing: letterSpacing.body,    fontWeight: fontWeight.medium },
  eyebrow:     { fontFamily: fontFamily.sans, fontSize: fontSize.micro,       lineHeight: lineHeight.body,    letterSpacing: letterSpacing.eyebrow, fontWeight: fontWeight.semibold, textTransform: 'uppercase' },
} as const satisfies Record<string, TypeStyle>;

export type TextStyleKey = keyof typeof textStyle;
